import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { TaskComment } from '../types/tasks';

interface CommentsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TaskComment[];
}

// Hook for fetching task comments
export const useTaskComments = (taskId: number | string, enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: ['taskComments', taskId], initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get<CommentsResponse | TaskComment[]>(`/api/task-comments/?task=${taskId}&page=${pageParam}`);
      return Array.isArray(response.data) ? { results: response.data, count: response.data.length, next: null } : response.data;
    },
    getNextPageParam: (last, pages) => last.next ? pages.length + 1 : undefined,
    enabled: enabled && !!taskId,
  });
  return { ...query, data: query.data?.pages.flatMap(page => page.results), count: query.data?.pages[0].count };

};

// Hook for comment mutations
export const useCommentMutations = () => {
  const queryClient = useQueryClient();

  const createCommentMutation = useMutation({
    mutationFn: async ({ taskId, content }: { taskId: number; content: string }) => {
      const response = await apiClient.post('/api/task-comments/', {
        task: taskId,
        content,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
      // Invalidate single task query
      queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      // Invalidate all task list queries to update comments_count
      queryClient.invalidateQueries({ queryKey: ['opportunityTasks'] });
      queryClient.invalidateQueries({ queryKey: ['jobTasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ 
      commentId, 
      content, 
      taskId 
    }: { 
      commentId: number; 
      content: string; 
      taskId: number;
    }) => {
      const response = await apiClient.patch(`/api/task-comments/${commentId}/`, {
        content,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async ({ commentId, taskId }: { commentId: number; taskId: number }) => {
      await apiClient.delete(`/api/task-comments/${commentId}/`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
      // Invalidate single task query
      queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      // Invalidate all task list queries to update comments_count
      queryClient.invalidateQueries({ queryKey: ['opportunityTasks'] });
      queryClient.invalidateQueries({ queryKey: ['jobTasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  return {
    createComment: createCommentMutation,
    updateComment: updateCommentMutation,
    deleteComment: deleteCommentMutation,
  };
};